import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link as RouterLink, Navigate, useNavigate, useParams } from 'react-router-dom';
import { Box, Container, Stack, Typography, Button } from '@mui/material';
import { WhatsApp, ArrowBack, ArrowForward, PictureAsPdfOutlined, CheckCircle } from '@mui/icons-material';
import AyudaShell from './AyudaShell';
import { useCursoProgress } from './cursoProgress';
import { useHeadMeta } from './useHeadMeta';
import { LECCIONES, MODULOS, findLeccion, isoDuracion } from './curso';
import { CREAM_SOFT, DISPLAY, INDIGO, INK, INK_MUTED, MONO, RULE, RULE_HARD, SANS, YELLOW, whatsappLink } from '../../components/public/publicTokens';

const AUTO_NEXT_SECONDS = 5;

const pillSolid = {
  bgcolor: INK,
  color: CREAM_SOFT,
  borderRadius: 999,
  px: 3,
  py: 1.3,
  fontFamily: SANS,
  fontWeight: 600,
  fontSize: '0.9rem',
  textTransform: 'none' as const,
  '&:hover': { bgcolor: INDIGO },
};
const pillOutline = {
  color: INK,
  borderRadius: 999,
  px: 3,
  py: 1.2,
  fontFamily: SANS,
  fontWeight: 600,
  fontSize: '0.9rem',
  textTransform: 'none' as const,
  border: `1px solid ${RULE_HARD}`,
  '&:hover': { borderColor: INK, bgcolor: 'rgba(10,10,10,0.04)' },
};

const LessonView: React.FC<{ slug: string }> = ({ slug }) => {
  const navigate = useNavigate();
  const { vistas, marcarVista } = useCursoProgress();
  const leccion = findLeccion(slug)!;
  const modulo = MODULOS[leccion.moduloIndex];
  const prev = LECCIONES[leccion.numero - 2];
  const next = LECCIONES[leccion.numero];
  const [countdown, setCountdown] = useState<number | null>(null);
  const timer = useRef<number | null>(null);

  const url = `https://dashflow.com.ar/ayuda/${leccion.slug}`;
  const howTo: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name: leccion.titulo,
    description: leccion.descripcion,
    inLanguage: 'es-AR',
    url,
    step: leccion.pasos.map((p, i) => ({ '@type': 'HowToStep', position: i + 1, text: p, url: `${url}#paso-${i + 1}` })),
  };
  if (leccion.video) {
    howTo.totalTime = isoDuracion(leccion.video.duracion);
    howTo.video = {
      '@type': 'VideoObject',
      name: leccion.titulo,
      description: leccion.descripcion,
      thumbnailUrl: `https://dashflow.com.ar/ayuda/${leccion.slug}.jpg`,
      contentUrl: `https://dashflow.com.ar/ayuda/${leccion.slug}.mp4`,
      duration: isoDuracion(leccion.video.duracion),
      ...(leccion.video.fecha ? { uploadDate: leccion.video.fecha } : {}),
    };
  }
  useHeadMeta({
    title: `${leccion.titulo} en DashFlow | Centro de ayuda`,
    description: leccion.descripcion,
    path: `/ayuda/${leccion.slug}`,
    jsonLd: [
      howTo,
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Centro de ayuda', item: 'https://dashflow.com.ar/ayuda' },
          { '@type': 'ListItem', position: 2, name: leccion.titulo, item: url },
        ],
      },
    ],
  });

  const clearTimer = useCallback(() => {
    if (timer.current !== null) window.clearInterval(timer.current);
    timer.current = null;
    setCountdown(null);
  }, []);

  // Al cambiar de lección: cortar cualquier cuenta regresiva y volver arriba.
  useEffect(() => {
    clearTimer();
    window.scrollTo(0, 0);
  }, [slug, clearTimer]);
  useEffect(() => () => clearTimer(), [clearTimer]);

  const irSiguiente = useCallback(() => {
    clearTimer();
    navigate(next ? `/ayuda/${next.slug}` : '/ayuda');
  }, [clearTimer, navigate, next]);

  const onVideoEnded = () => {
    marcarVista(leccion.slug);
    if (!next) return;
    setCountdown(AUTO_NEXT_SECONDS);
    timer.current = window.setInterval(() => {
      setCountdown((c) => (c === null ? null : Math.max(c - 1, 0)));
    }, 1000);
  };

  // Aviso cancelable: al llegar a 0 pasa sola a la próxima lección.
  useEffect(() => {
    if (countdown === 0) irSiguiente();
  }, [countdown, irSiguiente]);

  const vista = vistas.has(leccion.slug);

  return (
    <AyudaShell>
      <Container maxWidth="md" sx={{ px: { xs: 2, md: 3 }, pt: { xs: 4, md: 7 }, pb: { xs: 8, md: 12 } }}>
        <Typography sx={{ fontFamily: MONO, fontSize: '0.72rem', letterSpacing: '0.12em', color: INK_MUTED, textTransform: 'uppercase' }}>
          <Box component={RouterLink} to="/ayuda" sx={{ color: INK, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
            Curso de DashFlow
          </Box>{' '}
          / Módulo {leccion.moduloIndex + 1} · {modulo.nombre}
        </Typography>
        <Typography sx={{ fontFamily: MONO, fontSize: '0.72rem', color: INK_MUTED, mt: 1 }}>
          Lección {leccion.numero} de {LECCIONES.length}
          {vista ? ' · vista' : ''}
          {leccion.plan === 'pago' ? ' · disponible en los planes pagos' : ''}
        </Typography>

        <Typography component="h1" sx={{ fontFamily: DISPLAY, fontWeight: 400, fontSize: { xs: '2.3rem', md: '3.4rem' }, lineHeight: 1.05, mt: 1.5 }}>
          {leccion.titulo}
        </Typography>
        <Typography sx={{ fontFamily: SANS, fontSize: { xs: '1.02rem', md: '1.12rem' }, color: INK_MUTED, mt: 1.5 }}>{leccion.descripcion}</Typography>
        <Typography sx={{ fontFamily: SANS, fontSize: '0.88rem', color: INK_MUTED, mt: 1.5 }}>
          También sirve si buscás: {leccion.sinonimos.join(', ')}.
        </Typography>

        {/* Video (fase 2) */}
        <Box sx={{ mt: 4, position: 'relative', borderRadius: '16px', overflow: 'hidden', border: `1px solid ${RULE_HARD}`, bgcolor: INK, aspectRatio: '16 / 9' }}>
          {leccion.video ? (
            <Box
              component="video"
              src={`/ayuda/${leccion.slug}.mp4`}
              poster={`/ayuda/${leccion.slug}.jpg`}
              controls
              playsInline
              preload="metadata"
              onEnded={onVideoEnded}
              sx={{ width: '100%', height: '100%', display: 'block', objectFit: 'contain', bgcolor: INK }}
            />
          ) : (
            <Stack alignItems="center" justifyContent="center" sx={{ position: 'absolute', inset: 0, color: 'rgba(255,255,255,0.7)', textAlign: 'center', px: 3 }}>
              <Typography sx={{ fontFamily: MONO, fontSize: '0.72rem', letterSpacing: '0.2em', textTransform: 'uppercase', color: YELLOW }}>
                Video en preparación
              </Typography>
              <Typography sx={{ fontFamily: SANS, fontSize: '0.92rem', mt: 1, maxWidth: 420 }}>
                Mientras tanto, seguí el paso a paso de abajo: es lo mismo que vas a ver en el video.
              </Typography>
            </Stack>
          )}
          {countdown !== null && next && (
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1.5}
              alignItems={{ xs: 'stretch', sm: 'center' }}
              role="status"
              sx={{ position: 'absolute', left: 12, right: 12, bottom: 12, bgcolor: 'rgba(10,10,10,0.9)', color: CREAM_SOFT, borderRadius: '12px', p: 1.5 }}
            >
              <Typography sx={{ fontFamily: SANS, fontSize: '0.9rem', flex: 1 }}>
                Sigue «{next.titulo}» en {countdown} s
              </Typography>
              <Button onClick={clearTimer} sx={{ ...pillOutline, color: CREAM_SOFT, borderColor: 'rgba(255,255,255,0.3)', py: 0.6 }}>
                Cancelar
              </Button>
              <Button onClick={irSiguiente} sx={{ ...pillSolid, bgcolor: YELLOW, color: INK, py: 0.6, '&:hover': { bgcolor: '#fff' } }}>
                Ir ahora
              </Button>
            </Stack>
          )}
        </Box>

        {/* Paso a paso */}
        <Typography component="h2" sx={{ fontFamily: DISPLAY, fontSize: { xs: '1.8rem', md: '2.2rem' }, mt: { xs: 5, md: 6 } }}>
          Paso a paso
        </Typography>
        <Typography sx={{ fontFamily: SANS, fontSize: '0.9rem', color: INK_MUTED }}>Lo mismo que en el video, escrito.</Typography>
        <Box component="ol" sx={{ listStyle: 'none', p: 0, m: 0, mt: 2.5 }}>
          {leccion.pasos.map((p, i) => (
            <Box component="li" id={`paso-${i + 1}`} key={i} sx={{ display: 'grid', gridTemplateColumns: '36px minmax(0,1fr)', gap: 1.5, py: 1.6, borderTop: `1px solid ${RULE}` }}>
              <Box sx={{ width: 30, height: 30, borderRadius: '50%', bgcolor: YELLOW, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: MONO, fontSize: '0.82rem', fontWeight: 500 }}>
                {i + 1}
              </Box>
              <Typography sx={{ fontFamily: SANS, fontSize: '1rem', lineHeight: 1.55, pt: '3px' }}>{p}</Typography>
            </Box>
          ))}
        </Box>

        {/* Manual en PDF (fase 2: se muestra solo cuando la lección tiene pdf: true) */}
        {leccion.pdf && (
          <Box sx={{ mt: 4, p: 3, borderRadius: '16px', bgcolor: CREAM_SOFT, border: `1px solid ${RULE_HARD}` }}>
            <Typography component="h2" sx={{ fontFamily: SANS, fontWeight: 600, fontSize: '1.05rem' }}>Para tener al lado</Typography>
            <Typography sx={{ fontFamily: SANS, fontSize: '0.9rem', color: INK_MUTED, mt: 0.5 }}>
              El manual tiene cada paso con la pantalla y el lugar donde se toca. Se imprime en hojas A4.
            </Typography>
            <Button component="a" href={`/ayuda/${leccion.slug}.pdf`} download startIcon={<PictureAsPdfOutlined />} sx={{ ...pillOutline, mt: 2 }}>
              Bajar el manual en PDF
            </Button>
          </Box>
        )}

        {/* Marcar vista */}
        {!leccion.video && (
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mt: 4 }}>
            <Button
              onClick={() => {
                marcarVista(leccion.slug);
                irSiguiente();
              }}
              startIcon={<CheckCircle />}
              sx={pillSolid}
            >
              {next ? 'Marcar como vista y seguir' : 'Marcar como vista y terminar'}
            </Button>
          </Stack>
        )}

        {/* Dudas */}
        <Box sx={{ mt: 5, p: 3, borderRadius: '16px', bgcolor: INK, color: CREAM_SOFT }}>
          <Typography component="h2" sx={{ fontFamily: DISPLAY, fontSize: '1.7rem', lineHeight: 1.1 }}>¿Te quedó alguna duda?</Typography>
          <Typography sx={{ fontFamily: SANS, fontSize: '0.92rem', color: 'rgba(255,255,255,0.7)', mt: 0.8 }}>
            Escribinos por WhatsApp y alguien del equipo te ayuda a resolverlo.
          </Typography>
          <Button
            component="a"
            href={whatsappLink()}
            target="_blank"
            rel="noopener"
            startIcon={<WhatsApp />}
            sx={{ ...pillSolid, mt: 2, bgcolor: YELLOW, color: INK, '&:hover': { bgcolor: '#fff' } }}
          >
            Escribirnos
          </Button>
        </Box>

        {/* Anterior / siguiente */}
        <Box component="nav" aria-label="Lecciones" sx={{ mt: 5, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
          {prev ? (
            <Box component={RouterLink} to={`/ayuda/${prev.slug}`} sx={{ p: 2, borderRadius: '12px', border: `1px solid ${RULE_HARD}`, color: INK, textDecoration: 'none', '&:hover': { borderColor: INK } }}>
              <Stack direction="row" spacing={0.8} alignItems="center" sx={{ color: INK_MUTED }}>
                <ArrowBack sx={{ fontSize: 16 }} />
                <Typography sx={{ fontFamily: MONO, fontSize: '0.7rem', letterSpacing: '0.12em', textTransform: 'uppercase' }}>Anterior</Typography>
              </Stack>
              <Typography sx={{ fontFamily: SANS, fontWeight: 600, mt: 0.5 }}>{prev.titulo}</Typography>
            </Box>
          ) : (
            <span />
          )}
          {next && (
            <Box component={RouterLink} to={`/ayuda/${next.slug}`} sx={{ p: 2, borderRadius: '12px', border: `1px solid ${RULE_HARD}`, color: INK, textDecoration: 'none', textAlign: 'right', '&:hover': { borderColor: INK } }}>
              <Stack direction="row" spacing={0.8} alignItems="center" justifyContent="flex-end" sx={{ color: INK_MUTED }}>
                <Typography sx={{ fontFamily: MONO, fontSize: '0.7rem', letterSpacing: '0.12em', textTransform: 'uppercase' }}>Siguiente</Typography>
                <ArrowForward sx={{ fontSize: 16 }} />
              </Stack>
              <Typography sx={{ fontFamily: SANS, fontWeight: 600, mt: 0.5 }}>{next.titulo}</Typography>
            </Box>
          )}
        </Box>
      </Container>
    </AyudaShell>
  );
};

const AyudaLessonPage: React.FC = () => {
  const { slug = '' } = useParams();
  if (!findLeccion(slug)) return <Navigate to="/ayuda" replace />;
  return <LessonView slug={slug} />;
};

export default AyudaLessonPage;
